import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, ShoppingBag, Sparkles, History } from "lucide-react";
import { useAuth } from "../../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  giveaway_paid_ring: "Bought Love Rings",
  giveaway_essay: "Submitted Essay",
  giveaway_share: "Shared Contest",
  promoter_commission: "Promoter Commission",
  redemption: "Redeemed Reward",
};

export default function PointsWalletPage() {
  const { user } = useAuth();
  const email = user?.email;
  const [wallet, setWallet] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [redeeming, setRedeeming] = useState(null);

  async function loadWallet() {
    if (!email) return;
    try {
      const r = await axios.get(`${API}/points/wallet?email=${encodeURIComponent(email)}`);
      setWallet(r.data);
    } catch (e) { /* eslint-disable */ }
  }

  useEffect(() => {
    loadWallet();
    axios.get(`${API}/points/catalog`).then((r) => setCatalog(r.data || []));
  }, [email]);

  async function redeem(item) {
    if (!email) return toast.error("Sign in required.");
    if (!window.confirm(`Redeem ${item.label} for ${item.cost} points?`)) return;
    setRedeeming(item.item_id);
    try {
      const r = await axios.post(`${API}/points/redeem`, { email, item_id: item.item_id });
      toast.success(`Redeemed! New balance: ${r.data.new_balance} pts`);
      loadWallet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redemption failed.");
    } finally {
      setRedeeming(null);
    }
  }

  if (!email) {
    return <div className="text-sm text-black/50">Sign in to view your points.</div>;
  }

  const balance = wallet?.balance || 0;
  const history = wallet?.history || [];

  return (
    <div data-testid="points-wallet-page" className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Rewards</p>
        <h1 className="font-serif font-light text-4xl">Points Wallet</h1>
      </div>

      {/* Balance */}
      <div className="bg-gradient-to-br from-[#0f0d0a] to-[#1a1410] text-white border border-[#C8A96B]/30 rounded-2xl p-8 relative overflow-hidden">
        <Wallet className="absolute top-6 right-6 w-12 h-12 text-[var(--gold)] opacity-20" />
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Available balance</p>
        <p className="font-serif text-7xl text-[var(--gold)] tabular-nums" data-testid="wallet-balance">{balance.toLocaleString()}</p>
        <p className="text-sm text-white/60 mt-2">points</p>
      </div>

      {/* Redemption catalog */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="wallet-catalog">
        <h3 className="font-serif text-2xl mb-2 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[var(--gold)]" /> Redeem Rewards
        </h3>
        <p className="text-sm text-black/60 mb-6">Spend points on premium upgrades, marketing boosts, vendor discounts, and keepsakes.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {catalog.map((item) => {
            const canAfford = balance >= item.cost;
            return (
              <div key={item.item_id}
                className={`border rounded-xl p-4 ${canAfford ? "border-[var(--line)]" : "border-black/5 opacity-60"}`}>
                <p className="font-serif text-lg">{item.label}</p>
                <p className="text-[10px] uppercase tracking-widest text-black/50 mt-1">{item.category}</p>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[var(--gold)] font-serif text-xl">{item.cost.toLocaleString()} pts</p>
                  <button onClick={() => redeem(item)} disabled={!canAfford || redeeming === item.item_id}
                    className={`text-xs px-3 py-1.5 rounded-lg ${
                      canAfford ? "bg-[var(--gold)] text-black hover:opacity-90" : "bg-black/5 text-black/40"
                    }`}
                    data-testid={`redeem-${item.item_id}`}>
                    {redeeming === item.item_id ? "Redeeming…" : canAfford ? "Redeem" : "Not enough"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* History */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-[var(--gold)]" /> Activity
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-black/40 py-8 text-center">No activity yet. Refer friends and take part on Locked In Forever to earn points.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.ledger_id} className="flex items-center justify-between text-sm border-b border-[var(--line)]/60 py-2">
                <div>
                  <p className="font-medium">{SOURCE_LABELS[h.source] || h.source}</p>
                  <p className="text-[10px] text-black/40 uppercase tracking-widest">{h.created_at?.slice(0, 10)}</p>
                </div>
                <p className={`font-serif text-xl ${h.delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {h.delta >= 0 ? "+" : ""}{h.delta.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
